import {
  put,
  takeLatest,
  call,
  all,
  take,
  cancel,
  cancelled,
  spawn,
  delay,
  select,
} from 'redux-saga/effects';
import {
  HANDLE_SOCKET_LOGOUT_EXPIRED_SESSION,
  LOGIN,
  LOGIN_SUCCESS,
  START_TOKEN_RENEWAL,
  START_USER_IDLE_DETECTION,
  RENEW_USER_TOKEN,
  USER_IS_ACTIVE,
  USER_IS_IDLE,
  AZURE_SSO_LOGIN,
  AZURE_SSO_SELECT_USER,
  AZURE_SSO_SWITCH_USER,
} from './constants';
import { EXTEND_USER_SESSION } from '../App/constants';
import { LOG_OUT } from '../App/constants';
import {
  loginFail,
  clearMessages,
  loginSuccess,
  startTokenRenewal,
  startUserIdleDetection,
  tokenRenewalProcessStopped,
  renewUserToken,
  renewUserTokenSuccess,
  cancellingUserIdleProcess,
  azureSsoLoginSuccess,
  azureSsoLoginFail,
  azureSsoShowUserPicker,
  azureSsoHideUserPicker,
  azureSsoUpdateUserLastAccess,
} from './actions';
import {
  updateCurrentUser,
  selectProperty,
  showExtendUserSessionModal,
  hideExtendUserSessionModal,
  logOut,
  socketDisconnect,
  socketReAuthenticate,
} from '../App/actions';
import UserService from '../../services/userService';
import AzureAuthService from '../../services/azureAuthService';
import { userFacingError } from '../../services/azureAuthService';
import type { Action } from '../App/types';
import type { LoginPayload } from './types';
import { push } from 'react-router-redux';
import { omit } from 'ramda';
import type { Saga } from 'redux-saga';
import { pathOr } from 'ramda';
import { setDatadogUser } from '../../utils/datadog';
import { setCookie, getCookie } from '../../utils/cookie';
import {
  broadcastLogin,
  recordLocalLoginTimestamp,
} from '../../utils/crossTabActivity';

// Error message constant for unexpected/frontend errors
const GENERIC_ERROR_MESSAGE =
  'An unexpected error occurred. Please try again or contact your system administrator.';

export function* fetchLogin(action: Action<LoginPayload>): Saga<void> {
  try {
    const userService = new UserService();
    const { user, token, permissions, tokenTTL } = yield userService.login(
      omit(['rememberMe'], action.payload),
    );
    setDatadogUser(user);
    setCookie('session_id', token, tokenTTL);
    // Record login time on this tab and notify background tabs so they can
    // cancel any stale idle-logout countdowns still running from the old session.
    recordLocalLoginTimestamp();
    broadcastLogin(user.id);
    yield put(updateCurrentUser(user, permissions));
    if (user.properties.length === 1) {
      yield put(selectProperty(user.properties[0]));
    }
    yield put(loginSuccess());
    yield put(push('/'));
  } catch (err) {
    yield put(loginFail(err));
    yield delay(5000);
    yield put(clearMessages());
  }
}

export function* tokenRenewal(skipInitialRenewal?: boolean): Saga<void> {
  const userService = new UserService();

  const env = import.meta.env;
  // $FlowFixMe
  const getMinutes = () => {
    const minutes = env?.REACT_APP_TIME_MINUTES_RENEW_TOKEN
      ? parseInt(env.REACT_APP_TIME_MINUTES_RENEW_TOKEN, 10)
      : 25; // Changed from 1 to 25: Renew every 25 minutes (5 min before idle timeout, well before 6-hour expiration)
    return minutes;
  };

  const convertTimeToMilliseconds = 60000 * getMinutes();
  let consecutiveFailures = 0;
  const maxConsecutiveFailures = 5; // Increased from 3 to 5 for more resilience

  try {
    // On a fresh start fire immediately; on restart after Continue Session the
    // user-triggered extendUserSessionSaga already renewed, so wait a full interval.
    let nextDelay = skipInitialRenewal ? convertTimeToMilliseconds : 0;

    while (true) {
      yield delay(nextDelay);
      nextDelay = convertTimeToMilliseconds; // Reset to normal interval

      // Never renew in a background tab. The visible tab owns renewal;
      // when this tab becomes visible again the visibilitychange handler
      // will check session state and the next renewal cycle will run.
      if (typeof document !== 'undefined' && document.hidden) {
        if (import.meta.env.MODE === 'development') {
          // eslint-disable-next-line no-console
          console.log('Token renewal skipped: tab is in background');
        }
        continue;
      }

      const oldToken = getCookie('session_id');

      if (!oldToken) {
        // No token available, skip this renewal cycle
        if (import.meta.env.MODE === 'development') {
          // eslint-disable-next-line no-console
          console.warn('Token renewal skipped: No session token available');
        }
        continue;
      }

      try {
        const newToken = yield call(
          userService.extendUserSession,
          oldToken,
          true,
        );
        if (newToken && newToken.token && newToken.tokenTTL) {
          yield put(renewUserToken(newToken.token, newToken.tokenTTL));
          consecutiveFailures = 0; // Reset failure counter on success
        } else {
          throw new Error('Invalid token renewal response');
        }
      } catch (error) {
        consecutiveFailures += 1;

        if (import.meta.env.MODE === 'development') {
          // eslint-disable-next-line no-console
          console.error(
            `Token renewal failed (${consecutiveFailures}/${maxConsecutiveFailures}):`,
            error,
          );
        }

        // Don't give up! Use exponential backoff and keep trying
        if (consecutiveFailures < maxConsecutiveFailures) {
          // Exponential backoff: wait longer between retries (1min, 2min, 4min, 8min)
          nextDelay = Math.min(
            60000 * Math.pow(2, consecutiveFailures - 1),
            convertTimeToMilliseconds,
          );

          if (import.meta.env.MODE === 'development') {
            // eslint-disable-next-line no-console
            console.log(
              `Next token renewal attempt in ${nextDelay / 1000} seconds...`,
            );
          }
        } else {
          // After max failures, reset counter and use normal interval
          if (import.meta.env.MODE === 'development') {
            // eslint-disable-next-line no-console
            console.warn(
              `Token renewal had ${maxConsecutiveFailures} failures, resetting and continuing...`,
            );
          }
          consecutiveFailures = 0;
          nextDelay = convertTimeToMilliseconds;
        }
      }
    }
  } finally {
    if (yield cancelled()) yield put(tokenRenewalProcessStopped());
  }
}

export function* renewerUserTokenSaga(action: any): Saga<void> {
  setCookie('session_id', action.payload.token, action.payload.tokenTTL);
  yield put(socketReAuthenticate(action.payload.token));
  yield put(renewUserTokenSuccess());
}

export function* extendUserSessionSaga(): Saga<void> {
  try {
    const userService = new UserService();
    const oldToken = getCookie('session_id');
    if (!oldToken) {
      // No token available, can't extend
      return;
    }
    const newToken = yield call(userService.extendUserSession, oldToken);
    if (newToken && newToken.token && newToken.tokenTTL) {
      setCookie('session_id', newToken.token, newToken.tokenTTL);
      yield put(socketReAuthenticate(newToken.token));
      yield put(renewUserTokenSuccess());
      yield put(hideExtendUserSessionModal());
      yield put({ type: USER_IS_ACTIVE });
    }
  } catch (error) {
    if (import.meta.env.MODE === 'development') {
      // eslint-disable-next-line no-console
      console.error('Failed to extend user session:', error);
    }
    // Hide the modal and log out the user immediately to avoid confusion
    yield put(hideExtendUserSessionModal());
    yield put(logOut());
  }
}

export function* extendUserSessionWatcherSaga(): Saga<void> {
  yield takeLatest(EXTEND_USER_SESSION, extendUserSessionSaga);
}

export function* showUserIdleModal(minutes: number): Saga<void> {
  try {
    // Show modal immediately when idle is detected
    // The modal will handle its own countdown and user acknowledgment
    yield put(showExtendUserSessionModal());

    // Wait for the full idle timeout period
    // The logout will happen after this delay, but the modal is already shown
    yield delay(minutes);
  } finally {
    if (yield cancelled()) {
      yield put(hideExtendUserSessionModal());
    }
  }
}

export function* logOutUserIdle(minutes: number): Saga<void> {
  try {
    yield delay(minutes);
    yield put(socketDisconnect());
    yield put(loginFail('You have been logged out due to inactivity'));
    yield put(logOut());
  } finally {
    if (yield cancelled()) {
      yield put(cancellingUserIdleProcess());
    }
  }
}

export function* userIdleProcess(): Saga<void> {
  const env = import.meta.env;
  // $FlowFixMe
  const getMinutes = () =>
    pathOr(1, ['REACT_APP_IDLE_SESSION_EXTEND_WINDOW_MINUTES'], env);
  const minutes = 60000 * getMinutes();

  while (true) {
    yield take(USER_IS_IDLE);

    const tasks = yield all([
      spawn(showUserIdleModal, [minutes]),
      spawn(logOutUserIdle, [minutes]),
    ]);

    yield take([LOG_OUT, USER_IS_ACTIVE]);

    yield all(tasks.map((task) => cancel(task)));
  }
}

export function* userIdleBackgroundProcess(): Saga<void> {
  yield takeLatest(START_USER_IDLE_DETECTION, userIdleProcess);
}

export function* tokenBackgroundRenewallProcess(): Saga<void> {
  while (true) {
    yield take(START_TOKEN_RENEWAL);

    let renewalTask = yield spawn(tokenRenewal);

    while (true) {
      // Wait for logout or the user going idle (modal showing)
      const action = yield take([LOG_OUT, USER_IS_IDLE]);
      yield cancel(renewalTask);

      if (action.type === LOG_OUT) {
        break; // outer loop: await next START_TOKEN_RENEWAL
      }

      // USER_IS_IDLE: token renewal is suspended while the modal is open.
      // Wait for the user to extend the session or be logged out.
      const resumeAction = yield take([LOG_OUT, USER_IS_ACTIVE]);

      if (resumeAction.type === LOG_OUT) {
        break; // outer loop: await next START_TOKEN_RENEWAL
      }

      // USER_IS_ACTIVE (user clicked "Continue Session") — restart renewal.
      // Pass skipInitialRenewal=true: extendUserSessionSaga already fired an
      // extend-session call for this action, so skip the immediate first cycle
      // and wait the full 25-minute interval before the next background renewal.
      renewalTask = yield spawn(tokenRenewal, true);
    }
  }
}

export function* handleExpiredSocket(): Saga<void> {
  yield put(socketDisconnect());
  yield put(
    loginFail('You have been logged out due to to an inactive session'),
  );
  yield put(logOut());
}

export function* expiredSocketSessionSaga(): Saga<void> {
  yield takeLatest(HANDLE_SOCKET_LOGOUT_EXPIRED_SESSION, handleExpiredSocket);
}

export function* forkMaintenanceProcess(): Saga<void> {
  yield all([put(startTokenRenewal()), put(startUserIdleDetection())]);
}

export function* userMaintenanceSaga(): Saga<void> {
  yield takeLatest(LOGIN_SUCCESS, forkMaintenanceProcess);
}

export function* loginSaga(): Saga<void> {
  yield takeLatest(LOGIN, fetchLogin);
}

export function* userTokenWatcherSaga(): Saga<void> {
  yield takeLatest(RENEW_USER_TOKEN, renewerUserTokenSaga);
}

/**
 * Handle Azure SSO login flow
 *
 * Flow:
 * 1. Open Microsoft login popup
 * 2. User authenticates with Microsoft
 * 3. Exchange Azure token for Fortress JWT
 * 4. Store session and update Redux state
 * 5. Redirect to home page
 */
export function* fetchAzureSsoLogin(): Saga<void> {
  try {
    const azureAuthService = new AzureAuthService();

    // Step 1: Login with Microsoft using popup
    const azureAuthResult = yield call([azureAuthService, 'loginWithPopup']);

    if (!azureAuthResult || !azureAuthResult.idToken) {
      throw new Error(
        'Microsoft sign-in did not return an authentication token. Please try again.',
      );
    }

    // Step 2: Exchange Azure ID token for Fortress JWT
    const response = yield call(
      [azureAuthService, 'exchangeAzureTokenForFortressJWT'],
      azureAuthResult.idToken,
    );

    // Step 3: Check if user selection is required (Fortress SSO with multiple users)
    if (response.requiresUserSelection) {
      yield put(
        azureSsoShowUserPicker(response.users, azureAuthResult.idToken),
      );
      return; // Wait for user selection
    }

    // Step 4: Validate response structure to prevent destructuring errors
    if (!response || !response.user || !response.token) {
      throw userFacingError(GENERIC_ERROR_MESSAGE);
    }

    // Step 5: Normal login flow - extract response
    const { user, token, permissions, tokenTTL } = response;

    // Step 6: Store session and update Redux state
    setDatadogUser(user);
    setCookie('session_id', token, tokenTTL);
    // Record login time on this tab and notify background tabs so they can
    // cancel any stale idle-logout countdowns still running from the old session.
    recordLocalLoginTimestamp();
    broadcastLogin(user.id);
    yield put(updateCurrentUser(user, permissions));

    // Step 7: Select property if user has only one
    if (user.properties && user.properties.length === 1) {
      yield put(selectProperty(user.properties[0]));
    }

    // Step 8: Complete login and redirect
    yield put(azureSsoLoginSuccess());
    yield put(loginSuccess());
    yield put(push('/'));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Azure SSO login failed:', err);

    // Only show err.message when it was explicitly marked safe for the UI.
    // Library errors (e.g. MSAL, network) are Error instances too but their
    // messages are internal — those fall back to the generic string.
    const errorMessage =
      err?.isUserFacing === true ? err.message : GENERIC_ERROR_MESSAGE;

    yield put(azureSsoLoginFail(errorMessage));
    yield put(loginFail(errorMessage));
    yield delay(5000);
    yield put(clearMessages());
  }
}

/**
 * Handle user selection from the user picker modal
 * Re-submits the Azure token with the selected user ID
 */
export function* fetchAzureSsoSelectUser(action: any): Saga<void> {
  try {
    const azureAuthService = new AzureAuthService();

    // Get the Azure ID token from state (stored when user picker was shown)
    const azureIdToken = yield select((state) => state.login.azureIdToken);
    const selectedUserId = action.payload.userId;

    if (!azureIdToken) {
      throw userFacingError(GENERIC_ERROR_MESSAGE);
    }

    yield put(azureSsoHideUserPicker());

    // Exchange token again with selected user ID
    const response = yield call(
      [azureAuthService, 'exchangeAzureTokenForFortressJWT'],
      azureIdToken,
      selectedUserId,
    );

    // Validate response structure to prevent destructuring errors
    if (!response || !response.user || !response.token) {
      throw userFacingError(GENERIC_ERROR_MESSAGE);
    }

    // Extract response (should not have requiresUserSelection this time)
    const { user, token, permissions, tokenTTL } = response;

    setDatadogUser(user);
    setCookie('session_id', token, tokenTTL);
    yield put(updateCurrentUser(user, permissions));

    // Update the lastSystemAccess timestamp for this user in the cached list
    yield put(azureSsoUpdateUserLastAccess(selectedUserId));

    if (user.properties && user.properties.length === 1) {
      yield put(selectProperty(user.properties[0]));
    }

    yield put(azureSsoLoginSuccess());
    yield put(loginSuccess());
    yield put(push('/'));
  } catch (err) {
    console.error('Azure SSO user selection failed:', err);

    // Only show err.message when it was explicitly marked safe for the UI.
    // Library errors (e.g. MSAL, network) are Error instances too but their
    // messages are internal — those fall back to the generic string.
    const errorMessage =
      err?.isUserFacing === true ? err.message : GENERIC_ERROR_MESSAGE;

    yield put(azureSsoHideUserPicker());
    yield put(azureSsoLoginFail(errorMessage));
    yield put(loginFail(errorMessage));
    yield delay(5000);
    yield put(clearMessages());
  }
}

export function* azureSsoLoginSaga(): Saga<void> {
  yield takeLatest(AZURE_SSO_LOGIN, fetchAzureSsoLogin);
}

export function* azureSsoSelectUserSaga(): Saga<void> {
  yield takeLatest(AZURE_SSO_SELECT_USER, fetchAzureSsoSelectUser);
}

/**
 * Saga: Handle switch user request from logged-in state
 * Called when user clicks "Switch User" button in UserMenu
 */
export function* fetchAzureSsoSwitchUser(): Saga<void> {
  try {
    const azureAuthService = new AzureAuthService();

    // Get available data from state
    let azureIdToken = yield select((state) => state.login.azureIdToken);
    const availableUsers = yield select((state) => state.login.availableUsers);

    // If we have a valid token and user list, show picker immediately
    if (azureIdToken && availableUsers && availableUsers.length > 0) {
      yield put(azureSsoShowUserPicker(availableUsers, azureIdToken));
      return;
    }

    // Otherwise, trigger a new Azure login to get fresh token and user list
    // This will go through the normal SSO flow and show the picker if multiple users exist
    const response = yield call([azureAuthService, 'loginWithPopup']);
    azureIdToken = response.idToken;

    if (!azureIdToken) {
      throw userFacingError(GENERIC_ERROR_MESSAGE);
    }

    // Exchange token for Fortress JWT
    const loginResponse = yield call(
      [azureAuthService, 'exchangeAzureTokenForFortressJWT'],
      azureIdToken,
    );

    // If multiple users, show picker
    if (loginResponse.requiresUserSelection) {
      yield put(azureSsoShowUserPicker(loginResponse.users, azureIdToken));
      return;
    }

    // Validate response structure to prevent destructuring errors
    if (!loginResponse || !loginResponse.user || !loginResponse.token) {
      throw userFacingError(GENERIC_ERROR_MESSAGE);
    }

    // If only one user, complete login directly
    const { user, token, permissions, tokenTTL } = loginResponse;

    setDatadogUser(user);
    setCookie('session_id', token, tokenTTL);
    yield put(updateCurrentUser(user, permissions));

    if (user.properties && user.properties.length === 1) {
      yield put(selectProperty(user.properties[0]));
    }

    yield put(azureSsoLoginSuccess());
    yield put(loginSuccess());
  } catch (err) {
    console.error('Azure SSO switch user failed:', err);

    // Only show err.message when it was explicitly marked safe for the UI.
    // Library errors (e.g. MSAL, network) are Error instances too but their
    // messages are internal — those fall back to the generic string.
    const errorMessage =
      err?.isUserFacing === true ? err.message : GENERIC_ERROR_MESSAGE;

    yield put(azureSsoLoginFail(errorMessage));
  }
}

export function* azureSsoSwitchUserSaga(): Saga<void> {
  yield takeLatest(AZURE_SSO_SWITCH_USER, fetchAzureSsoSwitchUser);
}

export default [
  expiredSocketSessionSaga,
  loginSaga,
  userMaintenanceSaga,
  tokenBackgroundRenewallProcess,
  userTokenWatcherSaga,
  userIdleBackgroundProcess,
  userIdleProcess,
  extendUserSessionWatcherSaga,
  azureSsoLoginSaga,
  azureSsoSelectUserSaga,
  azureSsoSwitchUserSaga,
];
